/**
 * F37 (Wave 2D) — sport-scope multi-select used by both the badge + review-tag
 * Edit dialogs.
 *
 * `null` = all sports (the default); a non-empty array restricts the entry to
 * the listed sports. The "All sports" Switch toggles between the two modes;
 * when restricted, a Checkbox grid of the 41 sports is shown.
 *
 * Composes F02 primitives only (Switch + Checkbox).
 */
import { useI18n } from '@bravemove/i18n/react';
import { Switch, Checkbox } from '@bravemove/ui-web';
import { SPORT_NAME_VALUES, type SportName } from '@bravemove/types';

export interface SportScopePickerProps {
  /** `null` = all sports; else the restricted list. */
  readonly value: readonly SportName[] | null;
  readonly onChange: (value: readonly SportName[] | null) => void;
  readonly disabled?: boolean;
}

function sportLabel(s: string): string {
  return s.replace(/-/g, ' ');
}

export function SportScopePicker({ value, onChange, disabled = false }: SportScopePickerProps) {
  const { t } = useI18n();
  const allSports = value === null;
  const selected = new Set<string>(value ?? []);

  const toggleAll = (checked: boolean) => {
    // checked = "all sports" → null; unchecked = restricted (start empty).
    onChange(checked ? null : []);
  };

  const toggleSport = (sport: SportName) => {
    const next = new Set(selected);
    if (next.has(sport)) next.delete(sport);
    else next.add(sport);
    onChange([...next] as SportName[]);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="sport-scope-picker">
      <Switch
        checked={allSports}
        disabled={disabled}
        onChange={(e) => toggleAll(e.target.checked)}
        label={t('recommendation:catalog.sport_scope_all', { defaultValue: 'All sports' })}
        data-testid="sport-scope-all"
      />
      {!allSports ? (
        <div
          className="grid grid-cols-2 gap-x-4 gap-y-1.5 max-h-48 overflow-y-auto rounded-md border border-hairline bg-surface-2 p-3"
          data-testid="sport-scope-list"
        >
          {SPORT_NAME_VALUES.map((s) => (
            <Checkbox
              key={s}
              checked={selected.has(s)}
              disabled={disabled}
              onChange={() => toggleSport(s as SportName)}
              label={<span className="capitalize">{sportLabel(s)}</span>}
              data-testid={`sport-scope-${s}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
